import ExcelJs from 'exceljs';
import _ from 'lodash';

import { downloadFileToClient } from '@cdaas/reusable-components-daas';
import { COUNTRY_CALLING_CODES } from './constants';
import { sanitizeEmployeeId } from './validationUtils';
import { User, UserGroup } from '../types';

const COLUMNS = [{ name: 'Phone number', style: { numFmt: '@' } }, { name: 'Email' }, { name: 'Employee ID' }];
const COLUMNS_WITH_FEATURE_ENABLED = [
  { name: 'Phone number' },
  { name: 'Email' },
  { name: 'Employee ID' },
  { name: 'Onboarding date (dd.mm.yyyy)', style: { numFmt: 'dd.mm.yyyy' } },
];

const DONT_TOUCH_THIS_ROW = "Don't touch this row!";

const createWorksheetColumnsWithStyle = (worksheet: ExcelJs.Worksheet, userGroupName: string, columns: typeof COLUMNS) =>
  (worksheet.columns = [
    {
      header: 'User group',
      style: { numFmt: '@', ...(columns[0]?.style || {}) },
      width: 30,
    },
    {
      header: userGroupName,
      style: { numFmt: '@', ...(columns[1]?.style || {}) },
      width: 30,
    },
    ...Array.from({ length: columns.length - 2 }, (_, index) => ({
      header: '',
      style: { numFmt: '@', ...(columns[index + 2]?.style || {}) },
      width: 30,
    })),
    { header: DONT_TOUCH_THIS_ROW, width: 30 },
  ]);

const addColumnHeaders = (worksheet: ExcelJs.Worksheet, columns: typeof COLUMNS) =>
  worksheet.addRow([...columns.map(column => column.name), DONT_TOUCH_THIS_ROW]);

const makeColumnHeadersBold = (worksheet: ExcelJs.Worksheet, columns: typeof COLUMNS) =>
  columns.forEach((_, index) => (worksheet.getRow(2).getCell(index + 1).font = { bold: true }));

export const generateUsersWorkbook = (isPreScheduledOnboardingEnabled: boolean, userGroups: UserGroup[]): ExcelJs.Workbook => {
  const workbook = new ExcelJs.Workbook();
  const excelTabNameMaxLength = 31;
  const prohibitedCharactersInExcelTabName = ['\\', '/', '*', '?', ':', '[', ']'];
  const userGroupNamesAsExcelTabNames: string[] = [];

  userGroups.forEach(userGroup => {
    let userGroupNameAsExcelTabName = userGroup.name
      .split('')
      .map(char => (prohibitedCharactersInExcelTabName.includes(char) ? '_' : char))
      .join('')
      .substring(0, excelTabNameMaxLength);

    // Diff possible duplicate tab names
    let i = 1;
    const originalUserGroupNameAsExcelTabName = _.cloneDeep(userGroupNameAsExcelTabName);
    while (userGroupNamesAsExcelTabNames.includes(userGroupNameAsExcelTabName)) {
      userGroupNameAsExcelTabName = _.cloneDeep(originalUserGroupNameAsExcelTabName);
      const exceedsMaxTabNameLength = userGroupNameAsExcelTabName.length + i.toString().length - excelTabNameMaxLength;
      if (exceedsMaxTabNameLength > 0) {
        userGroupNameAsExcelTabName = userGroupNameAsExcelTabName.slice(0, userGroupNameAsExcelTabName.length - exceedsMaxTabNameLength);
      }
      userGroupNameAsExcelTabName += i.toString();
      i++;
    }
    userGroupNamesAsExcelTabNames.push(userGroupNameAsExcelTabName);

    const worksheet = workbook.addWorksheet(userGroupNameAsExcelTabName);

    const columns = isPreScheduledOnboardingEnabled ? COLUMNS_WITH_FEATURE_ENABLED : COLUMNS;
    createWorksheetColumnsWithStyle(worksheet, userGroup.name, columns);
    addColumnHeaders(worksheet, columns);
    makeColumnHeadersBold(worksheet, columns);
  });

  return workbook;
};

export const downloadUsersTemplateSpreadsheet = async (isPreScheduledOnboardingEnabled: boolean, userGroups?: UserGroup[]) => {
  if (!userGroups?.length) return;

  const workbook = generateUsersWorkbook(isPreScheduledOnboardingEnabled, userGroups);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadFileToClient(buffer, 'users.xlsx');
};

export const parsePhoneNumber = (phoneNumber: string): string => {
  // Removes any leading single quotes ('), equal signs (=), and all whitespace characters from the phone number
  phoneNumber = phoneNumber.replace(/^['=]+/, '').replace(/\s+/g, '');

  if (!/^\+?\d+$/.test(phoneNumber)) return '';

  const phoneNumberToCheck = phoneNumber.startsWith('+') ? phoneNumber.slice(1) : phoneNumber;
  const norwayCode = COUNTRY_CALLING_CODES.find(country => country.value === 'NO')?.code ?? '';

  if (phoneNumber.startsWith('+')) {
    return COUNTRY_CALLING_CODES.some(ccc => phoneNumberToCheck.startsWith(ccc.code)) ? phoneNumber : '';
  }

  let formattedPhoneNumber: string;
  if (phoneNumberToCheck.length <= 8) {
    formattedPhoneNumber = `+${norwayCode}${phoneNumberToCheck}`;
  } else if (COUNTRY_CALLING_CODES.some(ccc => phoneNumberToCheck.startsWith(ccc.code))) {
    formattedPhoneNumber = `+${phoneNumberToCheck}`;
  } else {
    formattedPhoneNumber = `+${norwayCode}${phoneNumberToCheck}`;
  }

  return formattedPhoneNumber;
};

export enum ExcelUserImportErrorType {
  NoUsers = 'no_users',
  FormatNotSupported = 'format_not_supported',
  General = 'general',
}

class ExcelUserImportError extends Error {
  type: ExcelUserImportErrorType;
  constructor(message: string | undefined, type: ExcelUserImportErrorType) {
    super(message);
    this.name = 'ExcelUserImportError';
    this.type = type;
  }
}

const parsePhoneNumberValue = (phone_number: unknown): string => {
  // Handle the case when the phone number is written with '+' in front of it when Excel handles it as a formula
  if (phone_number !== null && typeof phone_number === 'object') {
    phone_number = String((phone_number as { result: string | number }).result);
  }
  if (typeof phone_number !== 'string' && typeof phone_number !== 'number' && typeof phone_number !== 'undefined') {
    throw new ExcelUserImportError('Error: phone number not formatted as text or number', ExcelUserImportErrorType.FormatNotSupported);
  }
  return typeof phone_number === 'number' ? phone_number.toString() : (phone_number ?? '');
};

const parseEmailValue = (email_original: unknown): string => {
  const email = email_original && typeof email_original === 'object' && 'text' in email_original ? email_original.text : email_original;

  if (typeof email !== 'string' && typeof email !== 'undefined') {
    throw new ExcelUserImportError('Error: email not formatted as text', ExcelUserImportErrorType.FormatNotSupported);
  }
  return email ?? '';
};

const parseEmployeeIdValue = (employee_id: unknown): string => {
  if (typeof employee_id !== 'string' && typeof employee_id !== 'number' && typeof employee_id !== 'undefined') {
    throw new ExcelUserImportError('Error: employee id not formatted as text or number', ExcelUserImportErrorType.FormatNotSupported);
  }
  return typeof employee_id === 'number' ? employee_id.toString() : (employee_id ?? '');
};

const parseOnboardingDateValue = (onboarding_date: unknown): string => {
  if (!(onboarding_date instanceof Date || typeof onboarding_date === 'string' || onboarding_date === undefined)) {
    throw new ExcelUserImportError('Error: onboarding date not formatted as text', ExcelUserImportErrorType.FormatNotSupported);
  }

  if (onboarding_date instanceof Date) {
    const day = onboarding_date.getDate().toString().padStart(2, '0');
    const month = (onboarding_date.getMonth() + 1).toString().padStart(2, '0');
    const year = onboarding_date.getFullYear();
    return `${day}.${month}.${year}`;
  }
  return onboarding_date ?? '';
};

const parseRowValues = (
  row: ExcelJs.Row,
  isPreScheduledOnboardingEnabled: boolean,
): { phoneNumber: string; email: string; employeeId: string; onboardingDate: string } => {
  const [, phone_number, email_original, employee_id, onboarding_date] = row.values as (object | string | number)[];

  const phoneNumber = parsePhoneNumberValue(phone_number);
  const email = parseEmailValue(email_original);
  const employeeId = parseEmployeeIdValue(employee_id);
  const onboardingDate = isPreScheduledOnboardingEnabled ? parseOnboardingDateValue(onboarding_date) : '';

  return { phoneNumber, email, employeeId, onboardingDate };
};

const convertDateFormat = (value: string): string | undefined | null => {
  if (value.trim() === '') return null;

  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }

  return value; // Return the original value if it doesn't match the pattern
};

let counter = 0;

const createUser = (
  phoneNumber: string,
  email: string,
  employeeId: string,
  onboardingDate: string,
  userGroup: UserGroup | undefined,
  userGroupName: string,
  isPreScheduledOnboardingEnabled: boolean,
): User => ({
  name: '',
  phone_number: parsePhoneNumber(phoneNumber),
  id: `${Date.now()}-${counter++}`,
  email,
  employee_id: sanitizeEmployeeId(employeeId),
  prescheduled_onboarding_at: isPreScheduledOnboardingEnabled ? convertDateFormat(onboardingDate) : null,
  user_group: userGroup ?? {
    name: userGroupName,
    default_user_roles: [],
    inherited_invoicing_account: { name: '' },
  },
  user_roles: { effective: [], user: [], user_group: [] },
  allowed_procurements: [],
  allow_access_without_bank_id: false,
  inherited_invoicing_account: { name: '' },
});

export const importUsersSpreadsheet = async (
  isPreScheduledOnboardingEnabled: boolean,
  file?: File,
  userGroups?: UserGroup[],
  successCallback?: (users: User[]) => void,
  errorCallback?: (error: ExcelUserImportErrorType) => void,
) => {
  if (!userGroups || !file) return;
  const fileReader = new FileReader();
  fileReader.readAsArrayBuffer(file);
  fileReader.onload = async () => {
    try {
      const workbook = new ExcelJs.Workbook();
      const buffer = fileReader.result as ExcelJs.Buffer;
      if (buffer) {
        await workbook.xlsx.load(buffer);
        const users: User[] = [];
        for (const worksheet of workbook.worksheets) {
          const userGroupName = worksheet.getRow(1).getCell(2).text;
          const userGroup = userGroups.find(userGroup => userGroup.name === userGroupName);
          worksheet.eachRow((row, rowIndex) => {
            if (rowIndex < 3) return true;

            const { phoneNumber, email, employeeId, onboardingDate } = parseRowValues(row, isPreScheduledOnboardingEnabled);
            const user = createUser(phoneNumber, email, employeeId, onboardingDate, userGroup, userGroupName, isPreScheduledOnboardingEnabled);
            users.push(user);
          });
        }
        if (users.length === 0 && errorCallback) {
          throw new ExcelUserImportError('Error: no users in given excel template', ExcelUserImportErrorType.NoUsers);
        } else if (successCallback) {
          successCallback(users);
        }
      } else {
        throw new ExcelUserImportError('Error: creating buffer of excel template file failed', ExcelUserImportErrorType.General);
      }
    } catch (error) {
      if (errorCallback) {
        if (error instanceof ExcelUserImportError && Object.values(ExcelUserImportErrorType).includes(error.type)) {
          errorCallback(error.type);
        } else {
          errorCallback(ExcelUserImportErrorType.General);
        }
      }
    }
  };
};
